import { Box, Typography, Button, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { setLocalStorageItem } from '../../../Services/Global';
import { useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { createCardStyles, createSpacingStyles, createFlexStyles } from '../../../theme/layoutStyles';

/**
 * About component - Material UI version
 * Styled to match Dashboard layout and spacing
 *
 * @return {*} 
 */
export const About: React.FC = () => {
  const nav = useNavigate();
  const theme = useTheme();
  const cardStyles = createCardStyles(theme);
  const spacingStyles = createSpacingStyles(theme);
  const flexStyles = createFlexStyles(theme);

  useEffect(() => {
    setLocalStorageItem('showTutorial', 'false');
  }, []);

  // Styles matching Dashboard structure
  const styles = {
    container: {
      ...flexStyles.flexColumn,
      height: '100%',
      overflow: 'auto',
    },
    header: {
      padding: theme.spacing(2.5), // 20px equivalent
      flexShrink: 0,
    },
    headerTitle: {
      fontSize: '1.5rem',
      fontWeight: 500,
      margin: 0,
      color: theme.palette.text.primary,
    },
    mainSection: {
      ...cardStyles.sectionCard,
      margin: theme.spacing(2.5),
      marginTop: 0,
      marginBottom: theme.spacing(2.5),
    },
    sectionTitle: {
      fontSize: '1.25rem',
      fontWeight: 500,
      ...spacingStyles.marginBottom2,
      color: theme.palette.text.primary,
    },
    bodyText: {
      fontSize: '1rem',
      color: theme.palette.text.primary,
      lineHeight: 1.6,
      marginBottom: theme.spacing(2),
    },
    link: {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    footerSection: {
      margin: theme.spacing(2.5),
      marginTop: 0,
      ...flexStyles.flexCenter,
      justifyContent: 'center',
    },
    returnButton: {
      textTransform: 'none' as const,
      fontSize: '1rem',
      padding: theme.spacing(1.5, 3),
      minWidth: '120px',
    },
  };

  return (
    <Box sx={styles.container}>
      {/* Header */}
      <Box sx={styles.header}>
        <Typography variant="h4" sx={styles.headerTitle}>
          Welcome to Alfa Laval's AI Design Wizard for Plate Heat Exchangers.
        </Typography>
      </Box>

      {/* Main Content Section */}
      <Paper elevation={1} sx={styles.mainSection}>
        {/* <Typography variant="h5" sx={styles.sectionTitle}>
          Welcome to Alfa Laval's AI Design Wizard for Plate Heat Exchangers.
        </Typography> */}

        <Typography sx={styles.bodyText}>
          This is Alfa Laval's AI based Design Wizard for Plate Heat Exchangers that helps you to quickly find the right design for the specified heat exchanger positions.
        </Typography>
        
        <Typography sx={styles.bodyText}>
          The designs that the AI Design Wizard suggests are based on decades of experience and application expertise.
        </Typography>
        
        <Typography sx={styles.bodyText}>
          If you quote the AI Design Wizard's solution, Alfa Laval guarantees the thermal performance, because we know they work as intended.
          <br />
          For any questions or feedback regarding the AI Design Wizard, please reach out to your local Alfa Laval contact person or{' '}
          <Box
            component="a"
            href="mailto:AIDesignWizardFeedback@alfalaval.com?subject=[EMELIE][Feedback] - AI Design Wizard General Feedback"
            sx={styles.link}
          >
            use the feedback function in the app.
          </Box>
        </Typography>
        
        <Typography sx={styles.bodyText}>
          With your feedback we can make the AI Design Wizard better.
        </Typography>
        
        <Typography sx={styles.bodyText}>
          The AI Design Wizard will always calculate a competitive PHE design for the given duty.
          <br />
          For the models included in Alfa Laval's new hygienic line, H4, H8, etc., you copy and paste the result into the configurator by using the "copy" button.
        </Typography>
        
        <Typography sx={styles.bodyText}>
          The older models, Base6, Base10, etc., are still designed in CAS.
          <br />
          We are working on a solution to automatically export these results from the AI Design Wizard to CAS.
          <br />
          Until that's in place you can manually enter the AI Design Wizard's result into CAS.
        </Typography>
        
        <Typography sx={{ ...styles.bodyText, fontWeight: 500, marginBottom: 0 }}>
          Thank you for using the AI Design Wizard!
        </Typography>
      </Paper>

    </Box>
  );
}; 